import React, { useEffect, useState } from 'react';
import {
    GetFormalOffersResponse,
    GetMcaEstimatedScheduleResponse,
    LapiError,
    fetchGetMcaEstimatedSchedule,
    fetchPutFormalOffersMerchantCashAdvanceConfiguration
} from '@iwoca/lapi-client/edge';
import {
    AlertBox,
    Button,
    Card,
    Icon,
    Input,
    Slider,
    Spinner,
    Tooltip
} from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import styles from './rblCard.module.css';
import useDebounce from '../../../../../customHooks/useDebounce';
import classNames from 'classnames';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { FormattedMessage, useIntl } from 'react-intl';
import Fetching from '../../../../ui/fetching/fetching';
import messages from './rblCard.messages';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../../drawDown/DrawDownHelper';
import { tracking } from '@iwoca/frontend-tracking-library';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: async (
        stateKey: string,
        offerId: string,
        amount: number,
        sweep: number
    ) => {
        await fetchPutFormalOffersMerchantCashAdvanceConfiguration({
            stateKey,
            offerId,
            body: {
                data: {
                    cash_amount: amount,
                    revenue_sweep_fraction: sweep
                }
            }
        });
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: amount,
                sweep: sweep
            })
        });
    }
});

type TRblCard = {
    offer: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][number];
    amount?: number;
} & ReturnType<typeof mapDispatchToProps>;

type TSchedule = GetMcaEstimatedScheduleResponse;

const convertSweepToPercentage = (sweep: number) => {
    return Math.round(sweep * 10000) / 100;
};

const convertSweepToDecimal = (sweep: number) => {
    return Math.round(sweep * 100) / 10000;
};

const RblCard = ({ offer, amount, startDrawdown }: TRblCard) => {
    const stateKey = useStateKey();
    const intl = useIntl();
    const minAmount = 2200;

    const offer_id = offer.offer_id!;
    const amountInRange =
        !!amount && amount >= minAmount && amount <= offer.max_amount!;
    const disabled = !amountInRange;

    const [collapsed, setCollapsed] = useState(true);
    const [displaySweep, setDisplaySweep] = useState<number>();
    const [sweep, setSweep] = useState<number>();
    const [sweepVals, setSweepVals] = useState({
        absMin: 0,
        min: 0,
        max: 0,
        absMax: 0
    });

    const { data: schedule, isFetching } = useQuery<TSchedule, LapiError>({
        queryKey: [
            'mcaEstimatedSchedule',
            stateKey,
            offer_id,
            amount,
            sweep,
            minAmount,
            offer.max_amount
        ],
        queryFn: (): Promise<TSchedule> => {
            const amountInRange =
                !!amount && amount >= minAmount && amount <= offer.max_amount!;

            if (!amountInRange) {
                return new Promise((res) => res(schedule!));
            } else {
                return fetchGetMcaEstimatedSchedule({
                    stateKey: stateKey,
                    offerId: offer_id,
                    query: {
                        cash_amount: amount,
                        sweep_fraction: sweep
                            ? convertSweepToDecimal(sweep)
                            : undefined
                    }
                });
            }
        },
        enabled: !!amount,
        keepPreviousData: true
    });

    const isFetchingOrOutOfRange = isFetching || !amountInRange;

    useEffect(() => {
        if (schedule) {
            if (sweep === undefined) {
                setDisplaySweep(
                    convertSweepToPercentage(
                        schedule.data.summary.sweep.maximum_sweep_fraction
                    )
                );
                setSweepVals((prevVals) => ({
                    ...prevVals,
                    absMin: 0.1,
                    absMax: convertSweepToPercentage(
                        schedule.data.summary.sweep.maximum_sweep_fraction
                    )
                }));
            }
            setSweepVals((prevVals) => ({
                ...prevVals,
                min: convertSweepToPercentage(
                    schedule.data.summary.sweep.minimum_sweep_fraction
                ),
                max: convertSweepToPercentage(
                    schedule.data.summary.sweep.maximum_sweep_fraction
                )
            }));
        }
    }, [schedule, sweep, setSweep, setSweepVals]);

    useDebounce(() => setSweep(displaySweep), 800, [displaySweep]);

    // updates sweep to be in range if range ranges
    useEffect(() => {
        if (sweep) {
            if (sweepVals.min > sweep) {
                setDisplaySweep(sweepVals.min);
            }
            if (sweepVals.max < sweep) {
                setDisplaySweep(sweepVals.max);
            }
        }
    }, [sweepVals.min, sweepVals.max, sweep]);

    if (!amount || !sweep) {
        return (
            <Card className={styles.card}>
                <Spinner className={styles.spinner} />
            </Card>
        );
    }
    return (
        <Tooltip
            // @ts-expect-error
            content={
                <div className={styles.content}>
                    <FormattedMessage
                        {...messages.rblTooltip}
                        values={{
                            min: intlFormatCurrency({
                                intl,
                                value: minAmount
                            }),
                            max: intlFormatCurrency({
                                intl,
                                value: offer.max_amount!
                            })
                        }}
                    />
                </div>
            }
            open={disabled ? undefined : false}
            className={styles.tooltip}
        >
            <Card
                data-testid={`rblCard_${offer.offer_id}`}
                className={classNames(styles.card, {
                    [styles.disabled]: disabled
                })}
            >
                <h1 className={styles.title}>
                    <FormattedMessage
                        {...messages.title}
                        values={{
                            sweep: intl.formatNumber(sweep),
                            button: (chunks) => (
                                <button
                                    className={styles.titleButton}
                                    onClick={() => setCollapsed(!collapsed)}
                                    disabled={disabled}
                                >
                                    {chunks}
                                </button>
                            )
                        }}
                    />
                </h1>
                <AlertBox variant="success" className={styles.alert}>
                    <Icon icon="starFilled" className={styles.alertIcon} />
                    <span>
                        <FormattedMessage
                            {...messages.alert}
                            values={{
                                bold: (chunks) => <strong>{chunks}</strong>
                            }}
                        />
                    </span>
                </AlertBox>
                <div className={styles.info}>
                    <div className={styles.infoRow}>
                        <div>
                            <FormattedMessage {...messages.pricing} />
                        </div>
                        <div>
                            <FormattedMessage
                                {...messages.fixedFee}
                                values={{
                                    fee: (
                                        <Fetching
                                            isFetching={isFetchingOrOutOfRange}
                                        >
                                            {intlFormatCurrency({
                                                intl,
                                                value:
                                                    schedule?.data.summary
                                                        .funding_fee || 0
                                            })}
                                        </Fetching>
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.infoRow}>
                        <div>
                            <FormattedMessage {...messages.repayments} />
                        </div>

                        <div>
                            <FormattedMessage
                                {...messages.ofRev}
                                values={{
                                    percentage: intl.formatNumber(sweep),
                                    amount: intlFormatCurrency({
                                        intl,
                                        value:
                                            schedule?.data.estimated_schedule
                                                .estimated_monthly_repayment_amount ||
                                            0
                                    }),
                                    light: (chunks) => (
                                        <span className={styles.estMonthly}>
                                            {chunks}
                                        </span>
                                    ),
                                    fetching: (chunks) => (
                                        <Fetching
                                            isFetching={isFetchingOrOutOfRange}
                                        >
                                            {chunks}
                                        </Fetching>
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.infoRow}>
                        <div>
                            <FormattedMessage {...messages.total} />
                        </div>
                        <div>
                            <Fetching isFetching={isFetchingOrOutOfRange}>
                                {intlFormatCurrency({
                                    intl,
                                    value:
                                        schedule?.data.summary.total_payable ||
                                        0
                                })}
                            </Fetching>
                        </div>
                    </div>
                </div>

                <div
                    className={classNames(styles.collapsableContainer, {
                        [styles.collapsed]: collapsed
                    })}
                >
                    <div className={styles.collapsable} aria-hidden={collapsed}>
                        <Input
                            label={intl.formatMessage(messages.input)}
                            iconRight="percent"
                            value={displaySweep}
                            type="number"
                            step="0.01"
                            onChange={(ev) =>
                                setDisplaySweep(parseFloat(ev.target.value))
                            }
                            disabled={disabled}
                        />
                        <Slider
                            stops
                            step="0.01"
                            leftLabel={`${intl.formatNumber(
                                sweepVals.absMin
                            )}%`}
                            rightLabel={`${intl.formatNumber(
                                sweepVals.absMax
                            )}%`}
                            absMin={sweepVals.absMin}
                            absMax={sweepVals.absMax}
                            min={sweepVals.min}
                            max={sweepVals.max}
                            disabled={disabled}
                            value={displaySweep}
                            onChange={(ev) =>
                                setDisplaySweep(parseFloat(ev.target.value))
                            }
                        />
                        <p className={styles.legalText}>
                            <FormattedMessage
                                {...messages.legalText}
                                values={{
                                    period: schedule?.data.estimated_schedule
                                        .estimated_time_to_repay_in_months,
                                    bold: (chunks) => <strong>{chunks}</strong>,
                                    fetching: (chunks) => (
                                        <Fetching
                                            isFetching={isFetchingOrOutOfRange}
                                        >
                                            {chunks}
                                        </Fetching>
                                    )
                                }}
                            />
                        </p>
                    </div>
                    <div className={styles.collapseControll}>
                        <Button
                            variant="tertiary"
                            onClick={() => setCollapsed(!collapsed)}
                            disabled={disabled}
                        >
                            <FormattedMessage {...messages.customise} />
                            <Icon icon={collapsed ? 'angleDown' : 'angleUp'} />
                        </Button>
                        <Button
                            disabled={disabled}
                            onClick={() => {
                                tracking.ampli.drawdownCtaClicked();
                                void startDrawdown(
                                    stateKey,
                                    offer_id,
                                    amount,
                                    convertSweepToDecimal(sweep)
                                );
                            }}
                        >
                            <FormattedMessage {...messages.continue} />
                        </Button>
                    </div>
                </div>
            </Card>
        </Tooltip>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(RblCard);
